import React, { useState, useRef, useEffect } from 'react';
import { useFields } from './State';
import { DropDownOptions } from './DropDownOptions';

// Pending Removal Card
const PendingRemovalCard = ({ field, index, namePrefix }) => {
  const { undoRemoveField } = useFields();

  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
      <input type="hidden" name={`${namePrefix}[_destroy]`} value="1" />
      <input type="hidden" name={`${namePrefix}[id]`} value={field.id} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </div>
          <div>
            <p className="font-medium text-red-800">
              "{field.label || 'Untitled Field'}" will be removed
            </p>
            <p className="text-sm text-red-600">
              This change will take effect when you save
            </p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            undoRemoveField(field);
          }}
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
          </svg>
          Undo
        </button>
      </div>
    </div>
  );
};

export const FieldSetContainer = (props) => {
  const { removeField, setFieldType, state } = useFields();
  const { field, index, isFirst } = props;
  const [isCollapsed, setIsCollapsed] = useState(!field.isNew);
  const namePrefix = `${state.resource_name}[form_fields_attributes][${index}]`;
  const cardRef = useRef(null);

  // Scroll into view when newly added
  useEffect(() => {
    if (field.isNew && isFirst && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleRemoveClick = (e) => {
    e.preventDefault();
    removeField(field);
  };

  const handleInputTypeChange = (e) => {
    e.preventDefault();
    if (e.target.value === 'drop down') setFieldType(field, e.target.value);
  };

  // Show pending removal state
  if (field.destroy) {
    return <PendingRemovalCard field={field} index={index} namePrefix={namePrefix} />;
  }

  // Get input type label
  const getInputTypeLabel = (type) => {
    const labels = {
      'text': 'Text',
      'number': 'Number',
      'email': 'Email',
      'tel': 'Phone',
      'drop down': 'Dropdown',
      'checkbox': 'Checkbox',
      'textarea': 'Long Text',
    };
    return labels[type] || type || 'Text';
  };

  return (
    <div
      ref={cardRef}
      className={`bg-white border rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${
        field.isNew
          ? 'border-rose-300 ring-2 ring-rose-100'
          : 'border-gray-200'
      }`}
    >
      {field.id && (
        <input type="hidden" name={`${namePrefix}[id]`} value={field.id} />
      )}

      {/* Collapsed Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b cursor-pointer transition-colors ${
          field.isNew
            ? 'bg-rose-50 border-rose-200 hover:bg-rose-100'
            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
        }`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${
            field.input_type === 'checkbox' ? 'bg-purple-100' :
            field.input_type === 'drop down' ? 'bg-blue-100' :
            'bg-gray-100'
          }`}>
            {field.input_type === 'checkbox' ? (
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            ) : field.input_type === 'drop down' ? (
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">
              {field.label || 'New Custom Field'}
            </span>
            <span className="text-sm text-gray-500">
              ({getInputTypeLabel(field.input_type)})
            </span>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {field.isNew && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 animate-pulse">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                New - Unsaved
              </span>
            )}
            {field.required && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                Required
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {!isCollapsed && (
        <div className="p-4 space-y-4">
          {/* Field Name and Type Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={field.input_type !== 'checkbox' ? 'sm:col-span-2' : 'sm:col-span-2'}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Label <span className="text-red-500">*</span>
              </label>
              <input
                name={`${namePrefix}[label]`}
                defaultValue={field.label}
                disabled={field.disable_change}
                placeholder={
                  field.input_type === 'checkbox'
                    ? 'e.g., I agree to the membership terms'
                    : 'e.g., Member ID, Department, Start Date'
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors disabled:bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Type
              </label>
              <select
                name={`${namePrefix}[input_type]`}
                defaultValue={field.input_type}
                onChange={handleInputTypeChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white transition-colors"
              >
                {state.input_types.map((ip) => (
                  <option value={ip} key={ip}>
                    {ip.charAt(0).toUpperCase() + ip.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Placeholder (not for checkbox) */}
          {field.input_type !== 'checkbox' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Placeholder Text
              </label>
              <input
                name={`${namePrefix}[placeholder]`}
                defaultValue={field.placeholder}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
                placeholder="e.g., Enter your member ID..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Example text shown in the field before user types
              </p>
            </div>
          )}

          {/* Required Toggle */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex items-center h-5 mt-0.5">
                <input type="hidden" name={`${namePrefix}[required]`} value="false" />
                <input
                  id={`${field.objectId}Required`}
                  type="checkbox"
                  name={`${namePrefix}[required]`}
                  defaultChecked={field.required}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors"
                />
              </div>
              <div>
                <label
                  htmlFor={`${field.objectId}Required`}
                  className="block text-sm font-medium text-gray-900"
                >
                  {field.input_type === 'checkbox'
                    ? 'Require users to check this box'
                    : 'Make this field required'}
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {field.input_type === 'checkbox'
                    ? 'Users must check this box before submitting'
                    : 'Users must fill out this field before submitting'}
                </p>
              </div>
            </div>
          </div>

          {/* Dropdown Options */}
          {field.input_type === 'drop down' && (
            <DropDownOptions field={field} fieldIndex={index} />
          )}

          {/* Remove Button */}
          <div className="flex justify-end pt-2 border-t border-gray-200">
            <button
              onClick={handleRemoveClick}
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              {field.isNew ? 'Discard' : 'Remove Field'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
